import React from 'react';
import { HashRouter, Route } from 'react-router-dom';

import _ from 'lodash';

import {
  queryGetRate,
  queryCreateRate,
  queryUpdateRate,
  queryListBillingProfiles
} from '../../../Queries';

import PageNavigationLinks from '../../PageNavigationLinks';
import PageNavigationLink from '../../PageNavigationLink';

import ProcessingIndicator from '../../Common/ProcessingIndicator';
import ActionButton from '../../Common/ActionButton';

import PageHeader from '../../Common/PageHeader';

import NavigationBreadcrumbs from '../../Common/NavigationBreadcrumbs';
import { getBillingDashboardPageBreadcrumb } from '../Billing/BillingDashboardPage';
import { getRateListPageBreadcrumb } from './RateListPage';

import RateDetailGeneralPanel from './RateDetailGeneralPanel';
import RateDetailBillingPanel from './RateDetailBillingPanel';
import RateDetailFeesPanel from './RateDetailFeesPanel';
import RateDetailSalesPanel from './RateDetailSalesPanel';
import RateDetailAccessSystemPanel from './RateDetailAccessSystemPanel';

import Rate from '../../../Entities/Rate';
import AccessDevice from '../../../Entities/AccessDevice';
import BillingProfile from '../../../Entities/BillingProfile';
import SalesLimit from '../../../Entities/SalesLimit';

import '../Page.scss';
import './RateDetailPage.scss';

class RateDetailPage extends React.Component {
  state = {
    pageInitializationComplete: false,
    pageInitializationErrors: [],
    rateNewMode: true,
    rateEditMode: false,
    rateSaveInProgress: false,
    rateSaveErrors: [],
    allowEdit: true,
    rate: null
  };

  fieldConfiguration = {
    rate: null,
    accessDevice: null
  };

  editModeStoredValue = {
    rate: null
  };

  facilityId = null;
  rateId = null;

  billingProfiles = [];

  rateQueryFields = [
    'id',
    'billing_profile_id',
    'accessSystemMeta',
    'name',
    'description',
    'active',
    'display',
    'pass_type',
    'access_hours',
    'location',
    'require_car_info',
    'first_month_proration',
    'first_month_proration_day',
    'tax_exempt',
    'access_device_message',
    'lost_device_request',
    'months_to_sell_in_advance',
    { 'fees': [
      'id',
      'type',
      'price',
      'tax_1',
      'tax_2',
      'tax_3',
      'tax_4',
      'gross_price',
      'start_date'
    ] },
    { 'billing_profile': [
      { 'facility': [
        'id',
        'uid',
        'facility_name'
      ] }
    ] },
    { 'promo_codes': [
      'id',
      'text',
      'use_count',
      'use_limit',
      'expires_on',
      'is_active'
    ] },
    { 'access_devices': [
      'id',
      'type',
      'required',
      'offered',
      { 'fee': [
          'id',
          'type',
          'price',
          'tax_1',
          'tax_2',
          'tax_3',
          'tax_4',
          'gross_price',
          'start_date'
      ] }
    ] },
    { 'sales_limits': [
      'month',
      'year',
      'limit'
    ] }
  ];

  billingProfileQueryFields = [
    { 'billing_profiles': [
      'id',
      'name',
      'active',
      'billing_timing',
      'decline_recurring',
      { 'facility': [
        'id',
        'uid',
        'facility_name'
      ] }
    ] }
  ];


  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);

    const facilityId = urlSearchParams.get('facility');
    this.facilityId = (facilityId !== null) ? facilityId : null;

    const rateId = urlSearchParams.get('rate');
    this.rateId = (rateId !== null) ? rateId : null;
  }


  componentDidMount() {

    const initializationTasks = [];

    initializationTasks.push(new Promise((resolve, reject) => {
      Rate.getFieldAllowedValues().then(data => {
        this.fieldConfiguration.rate = {...data, access_hours: {options: data.access_hours.options.map((ah) => ({
          value: ah.value, label: ah.label === 'Twenty Four Seven Access' ? 
          '24/7 Access' : 
          ah.label === 'Monday through Friday Weekday Daytime' ? 
          'M-F Weekday Daytime' : 
          'Nights and Weekends'}))}};
        resolve();
      }, errors => {
        reject(errors);
      });
    }));

    initializationTasks.push(new Promise((resolve, reject) => {
      AccessDevice.getFieldAllowedValues().then(data => {
        this.fieldConfiguration.accessDevice = data;
        resolve();
      }, errors => {
        reject(errors);
      });
    }));

    initializationTasks.push(new Promise((resolve, reject) => {
      if (this.facilityId === null) {
        reject(['Missing Facility ID']);
        return;
      }

      queryListBillingProfiles({
        filters: [
          {
            model: 'BILLING_PROFILE',
            field: 'facility_id',
            op: 'EQ',
            value: `${this.facilityId}`
          },
          {
            model: 'BILLING_PROFILE',
            field: 'active',
            op: 'EQ',
            value: 'true'
          }
        ]
      }, this.billingProfileQueryFields).then(response => {
        this.billingProfiles = response.data.listBillingProfiles.billing_profiles.map(billingProfile => {
          return BillingProfile.createFromQueryValues(billingProfile);
        });
        resolve();
      }, errors => {
        reject(errors);
      });
    }));

    if (this.rateId !== null) {
      initializationTasks.push(new Promise((resolve, reject) => {
        this.fetchRate(this.rateId).then(rate => {
          this.setState({
            rate,
            rateNewMode: false,
            allowEdit: rate.isEditable(),
          }, () => {
            resolve();
          });
        }, errors => {
          reject(errors);
        });
      }));
    }
    else {
      initializationTasks.push(new Promise((resolve, reject) => {
        if (this.facilityId === null) {
          reject(['Missing Facility ID']);
          return;
        }

        Rate.createFromDefaultValuesForFacility(this.facilityId).then(rate => {
          rate.fields.sales_limits.value = SalesLimit.createFromDefaultValues(100);

          this.setState({
            rate,
            rateNewMode: true
          }, () => {
            resolve();
          });
        }, errors => {
          reject(errors);
        });
      }));
    }

    Promise.all(initializationTasks).then(() => {
      this.setState({
        pageInitializationComplete: true
      }, () => {
        if (!window.location.hash || window.location.hash === '#/') {
          window.location.hash = '#/general';
        }
      });
    }, errors => {
      this.setState({
        pageInitializationComplete: true,
        pageInitializationErrors: errors
      });
    });
  }


  fetchRate(rateId) {
    return new Promise((resolve, reject) => {
      queryGetRate({
        rate_id: rateId
      }, this.rateQueryFields).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }

        const rate = Rate.createFromQueryValues(response.data.getRate);
        resolve(rate);
      }, error => {
        reject([error]);
      });
    });
  }


  saveRate(rate) {
    return new Promise((resolve, reject) => {
      const {
        rateNewMode
      } = this.state;

      const validate = rate.validate(rateNewMode);
      if (!validate.valid) {
        reject([validate.error]);
        return;
      }

      const query = (rateNewMode) ? queryCreateRate : queryUpdateRate;
      const values = rate.getQueryFieldValues();

      query(values, this.rateQueryFields).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }

        const responseKey = Object.keys(response.data)[0];
        const rate = Rate.createFromQueryValues(response.data[responseKey]);

        if (this.editModeStoredValue.rate !== null) {
          this.editModeStoredValue.rate = rate;
        }

        resolve(rate);
      }, error => {
        reject([error]);
      });
    });
  }


  storeRateData = (inputId, value) => {
    const rate = _.cloneDeep(this.state.rate);

    switch (inputId) {
      case 'fees':
        rate.fields[inputId] = value;
        break;
      case 'accessLevelCode':
        rate.fields.accessSystemMeta.value.accessLevelCode = value;
        break;
      default:
        rate.fields[inputId].value = value;
    }

    this.setState({ rate });
  }


  onEditButtonClick = () => {
    this.enterRateEditMode(true);
  }


  onCancelButtonClick = () => {
    this.exitRateEditMode(true);
  }


  onSaveButtonClick = () => {
    const {
      rateNewMode
    } = this.state;

    this.setState({
      rateSaveInProgress: true,
      rateSaveErrors: []
    }, () => {
      this.saveRate(this.state.rate).then(rate => {
        if (rateNewMode) {
          const rateId = rate.fields.id.value;

          this.rateId = rateId;
          const url = `${window.location.origin}${window.location.pathname}${window.location.search}&rate=${rateId}${window.location.hash}`;
          window.history.replaceState(null, null, url);
        } else {
          this.exitRateEditMode(true)
        }

        this.setState({
          rate,
          rateSaveInProgress: false,
          rateNewMode: false
        });
      }, errors => {
        this.exitRateEditMode(false)
        this.setState({
          rateSaveInProgress: false,
          rateSaveErrors: errors
        });
      })
    });
  }


  enterRateEditMode(storeRate = true) {
    if (storeRate) this.editModeStoredValue.rate = this.state.rate;

    this.setState({
      rateEditMode: true
    });
  }


  exitRateEditMode(restoreRate = true) {
    const stateChanges = {
      rateEditMode: false
    };
    if (restoreRate) stateChanges.rate = this.editModeStoredValue.rate;

    this.setState(stateChanges);
  }


  renderNavigationBreadcrumbs() {
    const breadcrumbs = [];
    breadcrumbs.push(getBillingDashboardPageBreadcrumb(false, this.facilityId));
    breadcrumbs.push(getRateListPageBreadcrumb(false, this.facilityId));
    breadcrumbs.push(getRateDetailPageBreadcrumb(true));

    return (
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
    );
  }


  renderPageHeader() {
    const { rateNewMode } = this.state;

    const heading = `${(rateNewMode) ? 'Add New' : 'Edit'} Individual Rate`;

    return (
      <PageHeader heading={heading} />
    );
  }


  renderPageInitializationPanel() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page--user-message-container">
        {(!pageInitializationComplete) &&
          <ProcessingIndicator userMessage="Initializing, please wait..." />
        }
        {(pageInitializationComplete && pageInitializationErrors.length > 0) &&
          <div className="parkchirp--page--user-message">
            <p>The following errors occurred while initializing the page:</p>
            <ul>
              {pageInitializationErrors.map(error =>
                <li key={performance.now()}>{error}</li>
              )}
            </ul>
          </div>
        }
      </div>
    );
  }


  renderRatePanels() {
    const {
      rate,
      rateNewMode,
      rateEditMode
    } = this.state;

    return (
      <HashRouter>
        <PageNavigationLinks>
          <PageNavigationLink path="/general" label="General Settings" />
          <PageNavigationLink path="/billing" label="Billing &amp; Rates" />
          <PageNavigationLink path="/fees" label="Fees" />
          <PageNavigationLink path="/sales" label="Sales" />
          <PageNavigationLink path="/access-system" label="Access System" />
        </PageNavigationLinks>
        <React.Fragment>
          <Route path="/general" render={props => <RateDetailGeneralPanel
            {...props}
            rate={rate}
            fieldConfiguration={this.fieldConfiguration}
            newMode={rateNewMode}
            editMode={rateEditMode}
            onValueChange={this.storeRateData} />}
          />
          <Route path="/billing" render={props => <RateDetailBillingPanel
            {...props}
            rate={rate}
            billingProfiles={this.billingProfiles}
            fieldConfiguration={this.fieldConfiguration}
            newMode={rateNewMode}
            editMode={rateEditMode}
            onValueChange={this.storeRateData} />}
          />
          <Route path="/fees" render={props => <RateDetailFeesPanel
            {...props}
            rate={rate}
            billingProfiles={this.billingProfiles}
            fieldConfiguration={this.fieldConfiguration}
            newMode={rateNewMode}
            editMode={rateEditMode}
            onValueChange={this.storeRateData} />}
          />
          <Route path="/sales" render={props => <RateDetailSalesPanel
            {...props}
            rate={rate}
            fieldConfiguration={this.fieldConfiguration}
            newMode={rateNewMode}
            editMode={rateEditMode}
            onValueChange={this.storeRateData} />}
          />
          <Route path="/access-system" render={props => <RateDetailAccessSystemPanel
            {...props}
            rate={rate}
            newMode={rateNewMode}
            editMode={rateEditMode}
            onValueChange={this.storeRateData} />}
          />
        </React.Fragment>
      </HashRouter>
    );
  }


  renderSidebar() {
    const {
      rateNewMode,
      rateEditMode,
      rateSaveInProgress,
      rateSaveErrors
    } = this.state;

    return (
      <div className="parkchirp--sidebar-block">
        <div className="parkchirp--sidebar-block--actions">
          {(!rateNewMode && !rateEditMode) &&
            <button type="button"
              className="button button-primary button-large parkchirp--action"
              onClick={this.onEditButtonClick}>
              Edit
            </button>
          }
          {(rateNewMode || rateEditMode) &&
            <ActionButton label="Save"
              classNames={[ 'parkchirp--action' ]}
              inProgressState={rateSaveInProgress}
              onClick={this.onSaveButtonClick}
            />
          }
          {(rateEditMode && !rateNewMode) &&
            <button type="button"
              className="button button-primary button-large parkchirp--action"
              onClick={this.onCancelButtonClick}>
              Cancel
            </button>
          }
        </div>
        {(rateSaveErrors.length > 0) &&
          <div>
            <p>The following errors occurred while saving the Rate:</p>
            <ul>
              {rateSaveErrors.map(error =>
                <li key={performance.now()}>{error}</li>
              )}
            </ul>
          </div>
        }
      </div>
    );
  }


  render() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page parkchirp--page--rate-detail">
        {this.renderNavigationBreadcrumbs()}
        {this.renderPageHeader()}
        {(!pageInitializationComplete || pageInitializationErrors.length > 0) &&
          <div className="parkchirp--page-content">
            {this.renderPageInitializationPanel()}
          </div>
        }
        {(pageInitializationComplete && pageInitializationErrors.length === 0) &&
          <div className="parkchirp--page-content parkchirp--grid">
            <div className="parkchirp--grid-column parkchirp--grid-column-size--main-content">
              {this.renderRatePanels()}
            </div>
            <div className="parkchirp--grid-column parkchirp--grid-column-size--sidebar">
              {this.renderSidebar()}
            </div>
          </div>
        }
      </div>
    );
  }
}
export default RateDetailPage;


export function getRateDetailPageBreadcrumb(currentPage = false, rateId = null, facilityId = null) {
  const title = 'Individual Rate';
  const href = (currentPage) ? null :
    `?page=parkchirp_rate${(rateId !== null) ? `&rateId=${rateId}` : ''}${(facilityId !== null) ? `&facility=${facilityId}` : ''}`;

  return { title, href };
}
